import React from "react";
import { useFormContext } from "react-hook-form";

import { Label } from "app/components/ui/label";
import { Input } from "app/components/ui/input";
import { Checkbox } from "app/components/ui/checkbox";

type Props = {
  name: string;
};

export const NumberInformationForm = ({ name }: Props) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <React.Fragment>
      <div className="w-full bg-white p-4 sm:p-5 lg:p-6 border border-[#E5E7EB] rounded-xl md:rounded-2xl">
        <div className="flex items-center justify-between mb-4 sm:mb-5 lg:mb-6">
          <h3 className="text-[1.125rem] sm:text-[1.175rem] lg:text-xl font-bold leading-5 sm:leading-6 lg:leading-7 text-black font-mobile">
            {name === "transferInfo"
              ? "Number Transfer Information (Line 1)"
              : "New Number Information (Line 2)"}
          </h3>
          <div className="">
            <img
              src="/assets/chevron-down.svg"
              className="flex-shrink-0 w-4 h-4 transform rotate-180 sm:w-5 lg:w-6 sm:h-5 lg:h-6"
              alt="Chevron"
            />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-x-2 sm:gap-x-3 lg:gap-x-[0.815rem] gap-y-3.5 sm:gap-y-4 lg:gap-y-5">
          <div className="col-span-12 md:col-span-6">
            <Label
              htmlFor={`firstName-${name}`}
              className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              First name
            </Label>
            <Input
              id={`firstName-${name}`}
              type="text"
              {...register(`${name}.firstName`, {
                required: true,
              })}
              className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                (errors as any)?.[name]?.firstName
                  ? "border-[#D80027] focus:outline-[#D80027]"
                  : "border-[#D1D5DB] focus:outline-[#707C8F]"
              }`}
            />
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label
              htmlFor={`lastName-${name}`}
              className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              Last name
            </Label>
            <Input
              id={`lastName-${name}`}
              type="text"
              {...register(`${name}.lastName`, {
                required: true,
              })}
              className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                (errors as any)?.[name]?.lastName
                  ? "border-[#D80027] focus:outline-[#D80027]"
                  : "border-[#D1D5DB] focus:outline-[#707C8F]"
              }`}
            />
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label
              htmlFor={`address-${name}`}
              className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              Address
            </Label>
            <Input
              id={`address-${name}`}
              type="text"
              {...register(`${name}.address`, {
                required: true,
              })}
              className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                (errors as any)?.[name]?.address
                  ? "border-[#D80027] focus:outline-[#D80027]"
                  : "border-[#D1D5DB] focus:outline-[#707C8F]"
              }`}
            />
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label
              htmlFor={`apt-${name}`}
              className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              Apt or suite (optional)
            </Label>
            <Input
              id={`apt-${name}`}
              type="text"
              {...register(`${name}.apt`)}
              className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                (errors as any)?.[name]?.apt
                  ? "border-[#D80027] focus:outline-[#D80027]"
                  : "border-[#D1D5DB] focus:outline-[#707C8F]"
              }`}
            />
          </div>
          <div className="col-span-6 md:col-span-4">
            <Label
              htmlFor={`city-${name}`}
              className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              City
            </Label>
            <Input
              id={`city-${name}`}
              type="text"
              {...register(`${name}.city`, {
                required: true,
              })}
              className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                (errors as any)?.[name]?.city
                  ? "border-[#D80027] focus:outline-[#D80027]"
                  : "border-[#D1D5DB] focus:outline-[#707C8F]"
              }`}
            />
          </div>
          <div className="col-span-6 md:col-span-4">
            <Label
              htmlFor={`zipCode-${name}`}
              className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              Zip code
            </Label>
            <Input
              id={`zipCode-${name}`}
              type="text"
              inputMode="numeric"
              maxLength={5}
              {...register(`${name}.zipCode`, {
                required: true,
                pattern: {
                  value: /^[0-9]{5}$/,
                  message: "Please enter a valid 5-digit ZIP code",
                },
                onChange: (e) => {
                  e.target.value = e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 5);
                },
              })}
              className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                (errors as any)?.[name]?.zipCode
                  ? "border-[#D80027] focus:outline-[#D80027]"
                  : "border-[#D1D5DB] focus:outline-[#707C8F]"
              }`}
            />
          </div>
          <div className="col-span-6 md:col-span-4">
            <Label
              htmlFor={`state-${name}`}
              className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              State
            </Label>
            <Input
              id={`state-${name}`}
              type="text"
              {...register(`${name}.state`, {
                required: true,
              })}
              className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                (errors as any)?.[name]?.state
                  ? "border-[#D80027] focus:outline-[#D80027]"
                  : "border-[#D1D5DB] focus:outline-[#707C8F]"
              }`}
            />
          </div>
        </div>
        {name === "transferInfo" && (
          <>
            <p className="text-[#1D1D1B] text-[0.825rem] sm:text-sm lg:text-[0.9375rem] font-semibold leading-4 sm:leading-5 mt-5 sm:mt-7 md:mt-9 lg:mt-11 mb-2.5 sm:mb-3 lg:mb-[0.875rem]">
              This information is provided by your current carrier.
            </p>
            <div className="grid grid-cols-2 gap-x-2 sm:gap-x-3 lg:gap-x-[0.815rem] gap-y-3.5 sm:gap-y-4 lg:gap-y-5">
              <div className="">
                <Label
                  htmlFor={`accountNumber-${name}`}
                  className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
                >
                  Account Number
                </Label>
                <Input
                  id={`accountNumber-${name}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  {...register(`${name}.accountNumber`, {
                    required: true,
                  })}
                  className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                    (errors as any)?.[name]?.accountNumber
                      ? "border-[#D80027] focus:outline-[#D80027]"
                      : "border-[#D1D5DB] focus:outline-[#707C8F]"
                  }`}
                />
              </div>
              <div className="">
                <Label
                  htmlFor={`transferPin-${name}`}
                  className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
                >
                  Transfer Pin
                </Label>
                <Input
                  id={`transferPin-${name}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  {...register(`${name}.transferPin`, {
                    required: true,
                  })}
                  className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                    (errors as any)?.[name]?.transferPin
                      ? "border-[#D80027] focus:outline-[#D80027]"
                      : "border-[#D1D5DB] focus:outline-[#707C8F]"
                  }`}
                />
              </div>
              <div className="col-span-2">
                <Label
                  htmlFor={`numberToTransfer-${name}`}
                  className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
                >
                  Number to transfer
                </Label>
                <Input
                  id={`numberToTransfer-${name}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  {...register(`${name}.numberToTransfer`, {
                    required: true,
                  })}
                  className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline ${
                    (errors as any)?.[name]?.numberToTransfer
                      ? "border-[#D80027] focus:outline-[#D80027]"
                      : "border-[#D1D5DB] focus:outline-[#707C8F]"
                  }`}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-[0.625rem] mt-2 sm:mt-[0.625rem]">
              <Checkbox
                id="acceptTerms"
                className="w-4 sm:w-5 lg:w-6 h-4 sm:h-5 lg:h-6 rounded-[0.25rem]"
              />
              <Label
                htmlFor="acceptTerms"
                className="mb-0 text-[0.825rem] sm:text-sm font-normal leading-5 sm:leading-6 text-[#1D1D1B] cursor-pointer underline decoration-solid decoration-skip-ink decoration-[auto] underline-offset-[auto] [text-underline-position:from-font]"
              >
                I accept terms and conditions
              </Label>
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
};
